import React, { useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Modal, Button, Checkbox } from '../ui';
import { CaseFormEditor } from '../caseEntry/CaseFormEditor';
import { ImagePickerDialog } from '../caseEntry/ImagePickerDialog';
import { PCCase, FieldConfidence } from '../../types';
import { updateCase } from '../../services/caseService';
import { fetchPageContent } from '../../services/urlFetchService';
import { extractCaseData } from '../../services/aiExtractionService';

interface CaseEditModalProps {
  pcCase: PCCase;
  onClose: () => void;
  onSave: () => void;
}

// Fields to compare for change detection
const TRACKED_FIELDS = [
  'name', 'manufacturer', 'sizeCategory', 'weight',
  'maxGpuLength', 'maxCpuCoolerHeight', 'maxPsuLength',
  'releaseYear', 'description', 'imageUrl',
] as const;

const TRACKED_NESTED: [string, string][] = [
  ['dimensions', 'height'], ['dimensions', 'width'], ['dimensions', 'depth'],
];

function getChangedFields(before: Partial<PCCase>, after: Partial<PCCase>): Set<string> {
  const changed = new Set<string>();

  for (const field of TRACKED_FIELDS) {
    const oldVal = (before as any)[field];
    const newVal = (after as any)[field];
    if (newVal !== undefined && newVal !== null && String(newVal) !== String(oldVal ?? '')) {
      changed.add(field);
    }
  }

  for (const [parent, child] of TRACKED_NESTED) {
    const oldVal = (before as any)[parent]?.[child];
    const newVal = (after as any)[parent]?.[child];
    if (newVal !== undefined && newVal !== null && String(newVal) !== String(oldVal ?? '')) {
      changed.add(`${parent}.${child}`);
    }
  }

  // Array fields
  const arrayFields = ['sidePanel', 'colors', 'formFactorSupport', 'features'] as const;
  for (const field of arrayFields) {
    const oldArr = (before as any)[field] || [];
    const newArr = (after as any)[field] || [];
    if (JSON.stringify(oldArr) !== JSON.stringify(newArr) && newArr.length > 0) {
      changed.add(field);
    }
  }

  return changed;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name', manufacturer: 'Manufacturer', sizeCategory: 'Size Category',
  weight: 'Weight', maxGpuLength: 'Max GPU Length', maxCpuCoolerHeight: 'Max CPU Cooler Height',
  maxPsuLength: 'Max PSU Length', releaseYear: 'Release Year', description: 'Description',
  imageUrl: 'Image URL', 'dimensions.height': 'Height', 'dimensions.width': 'Width',
  'dimensions.depth': 'Depth', sidePanel: 'Side Panel', colors: 'Colors',
  formFactorSupport: 'Form Factors', features: 'Features',
};

export const CaseEditModal: React.FC<CaseEditModalProps> = ({ pcCase, onClose, onSave }) => {
  const [data, setData] = useState<Partial<PCCase>>({ ...pcCase });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [updatedFields, setUpdatedFields] = useState<Set<string>>(new Set());
  const [confidenceScores, setConfidenceScores] = useState<FieldConfidence[]>([]);
  const [allSet, setAllSet] = useState(pcCase.allSet ?? false);
  const [foundImages, setFoundImages] = useState<string[]>([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [isSavingImage, setIsSavingImage] = useState(false);

  const handleFetchImages = async () => {
    if (!data.productPageUrl) return;
    setIsFetchingImages(true);
    try {
      const res = await fetch('/api/image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'fetch', url: data.productPageUrl }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Fetch failed');
      setFoundImages(json.images);
      setShowImagePicker(true);
    } catch (err: any) {
      console.error('Image fetch failed:', err.message);
    } finally {
      setIsFetchingImages(false);
    }
  };

  const handleSaveImage = async (url: string) => {
    setIsSavingImage(true);
    try {
      const res = await fetch('/api/image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save', url }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Save failed');
      handleChange('imageUrl', json.localPath);
      setShowImagePicker(false);
    } catch (err: any) {
      console.error('Image save failed:', err.message);
    } finally {
      setIsSavingImage(false);
    }
  };

  const handleChange = (field: string, value: unknown) => {
    setData((prev) => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prev,
          [parent]: { ...(prev as any)[parent], [child]: value },
        };
      }
      return { ...prev, [field]: value };
    });
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleExtract = useCallback(async (url: string) => {
    setIsExtracting(true);
    setExtractError(null);
    setUpdatedFields(new Set());
    try {
      const fetchResult = await fetchPageContent(url);
      if (!fetchResult.success) {
        setExtractError(fetchResult.error || 'Failed to fetch page content');
        return;
      }

      const result = await extractCaseData(fetchResult.content!);
      if (!result.success) {
        setExtractError(result.error || 'Failed to extract case data');
        return;
      }

      const imageUrl = result.data.imageUrl || fetchResult.imageUrl || undefined;
      const extracted = {
        ...result.data,
        productPageUrl: url,
        ...(imageUrl ? { imageUrl } : {}),
      };

      setData((prev) => {
        const merged = { ...prev, ...extracted };
        const changed = getChangedFields(prev, merged);
        setUpdatedFields(changed);
        return merged;
      });

      setConfidenceScores(result.confidenceScores || []);
    } catch (error) {
      setExtractError(error instanceof Error ? error.message : 'Extraction failed');
    } finally {
      setIsExtracting(false);
    }
  }, []);

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!data.name) newErrors.name = 'Name is required';
    if (!data.manufacturer) newErrors.manufacturer = 'Manufacturer is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      await updateCase(pcCase.id, { ...data, allSet });
      onSave();
    } catch (error) {
      console.error('Failed to update case:', error);
      alert('Failed to update case');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`Edit: ${pcCase.manufacturer} ${pcCase.name}`} size="full">
      {/* Updated fields banner */}
      {updatedFields.size > 0 && (
        <div className="mb-6 rounded-lg border border-emerald-300 bg-emerald-50 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-emerald-800">
            <RefreshCw size={14} />
            {updatedFields.size} field{updatedFields.size > 1 ? 's' : ''} updated from product page — highlighted below
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Array.from(updatedFields).map((field) => (
              <span key={field} className="rounded-full border border-emerald-300 bg-surface px-2 py-0.5 text-xs font-medium text-emerald-800">
                {FIELD_LABELS[field] || field}
              </span>
            ))}
          </div>
        </div>
      )}

      <CaseFormEditor
        data={data}
        confidenceScores={confidenceScores}
        errors={errors}
        onChange={handleChange}
        onExtract={handleExtract}
        isExtracting={isExtracting}
        onFetchImages={handleFetchImages}
        isFetchingImages={isFetchingImages}
        updatedFields={updatedFields}
      />

      {extractError && (
        <div className="mt-4 rounded-lg border border-danger/40 bg-danger-soft p-3 text-sm text-danger">
          {extractError}
        </div>
      )}

      <div className="sticky bottom-0 -mx-6 -mb-6 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-line bg-surface px-6 py-4">
        <div className="flex flex-col gap-0.5">
          <Checkbox checked={allSet} onChange={setAllSet} label="Mark as reviewed (all set)" />
          <span className="pl-[26px] text-xs text-muted">Reviewed cases are hidden from the pending list.</span>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} isLoading={saving}>
            Save changes
          </Button>
        </div>
      </div>
      <ImagePickerDialog
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        images={foundImages}
        onSave={handleSaveImage}
        isSaving={isSavingImage}
      />
    </Modal>
  );
};
